import { Container, Graphics, Text, Assets, Texture, Sprite, Rectangle } from 'pixi.js';
import { PowerupType } from '@wooorms/shared';

/**
 * Powerup item for rendering
 */
export interface PowerupItem {
	id: string;
	x: number;
	y: number;
	radius: number;
	color: number;
	powerupType: PowerupType;
}

/**
 * Powerup state from server
 */
export interface PowerupState {
	id: string;
	x: number;
	y: number;
	radius: number;
	color: number;
	powerupType: string;
}

/**
 * Powerup renderer configuration
 */
export interface PowerupRendererConfig {
	/** Glow intensity (0 = no glow) */
	glowIntensity: number;
	/** Animation speed multiplier */
	animationSpeed: number;
	/** Whether to show pulse animation */
	enablePulse: boolean;
	/** Whether to show icon labels (legacy text) */
	showLabels: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: PowerupRendererConfig = {
	glowIntensity: 0.6,
	animationSpeed: 1,
	enablePulse: true,
	showLabels: false, // Default to false as we have sprites now
};

/**
 * Sprite sheet configuration
 */
const SPRITE_SHEET_PATH = '/assets/powerups.png';
const SPRITE_SIZE = 32; // Assuming 32x32 tiles

/**
 * Mapping of PowerupType to sprite sheet grid coordinates [row, col]
 */
const POWERUP_SPRITE_MAPPING: Record<string, [number, number]> = {
	// Row 0: Multipliers
	[PowerupType.MULTIPLIER_2X]: [0, 0],
	[PowerupType.MULTIPLIER_4X]: [0, 1],
	[PowerupType.MULTIPLIER_6X]: [0, 2],

	// Row 1: High Multipliers & Magnet
	[PowerupType.MULTIPLIER_10X]: [1, 0],
	[PowerupType.MULTIPLIER_15X]: [1, 1],
	[PowerupType.MAGNET]: [1, 2],

	// Row 2: Utility
	[PowerupType.SPEED]: [2, 0],
	[PowerupType.AGILITY]: [2, 1],
	[PowerupType.VISION]: [2, 2],
};

/**
 * PowerupRenderer - Renders powerup items in the world
 *
 * Features:
 * - Pulsing glow effect
 * - Sprite-based icons
 * - Color-coded by type
 */
export class PowerupRenderer {
	/** Main container for powerup items */
	public container: Container;

	/** Container for powerup sprites */
	private spriteContainer: Container;

	/** Configuration */
	private config: PowerupRendererConfig;

	/** Graphics object for batch rendering */
	private graphics: Graphics;

	/** Glow graphics (rendered below main powerup) */
	private glowGraphics: Graphics;

	/** Label container (legacy support) */
	private labelContainer: Container;

	/** Label cache */
	private labels: Map<string, Text> = new Map();

	/** Current powerup items */
	private powerups: Map<string, PowerupItem> = new Map();

	/** Sprite instances */
	private sprites: Map<string, Sprite> = new Map();

	/** Loaded sprite sheet texture */
	private texture: Texture | null = null;

	/** Animation time accumulator */
	private animTime: number = 0;

	/** Visibility bounds for culling */
	private visibleBounds: { left: number; right: number; top: number; bottom: number } | null = null;

	/** Dirty flag - true when state has changed */
	private dirty: boolean = true;

	constructor(config: Partial<PowerupRendererConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };

		// Create container
		this.container = new Container();
		this.container.label = 'powerup-renderer';

		// Create containers
		this.glowGraphics = new Graphics();
		this.graphics = new Graphics();
		this.spriteContainer = new Container();
		this.labelContainer = new Container();

		// Add to container (glow -> graphics -> sprites -> labels)
		this.container.addChild(this.glowGraphics);
		this.container.addChild(this.graphics);
		this.container.addChild(this.spriteContainer);
		this.container.addChild(this.labelContainer);

		// Load assets
		this.loadAssets();
	}

	/**
	 * Load sprite sheet texture
	 */
	private async loadAssets(): Promise<void> {
		try {
			this.texture = await Assets.load(SPRITE_SHEET_PATH);
			this.dirty = true;
		} catch (e) {
			console.error('Failed to load powerup sprites:', e);
		}
	}

	/**
	 * Set visible bounds for culling (in world coordinates)
	 */
	setVisibleBounds(left: number, right: number, top: number, bottom: number): void {
		this.visibleBounds = { left, right, top, bottom };
		this.dirty = true;
	}

	/**
	 * Clear visible bounds (render all powerups)
	 */
	clearVisibleBounds(): void {
		this.visibleBounds = null;
		this.dirty = true;
	}

	/**
	 * Update powerup items from server state
	 */
	updateFromState(powerups: Map<string, PowerupState>): void {
		const seenIds = new Set<string>();

		powerups.forEach((powerup, id) => {
			seenIds.add(id);
			const existing = this.powerups.get(id);
			if (existing) {
				existing.x = powerup.x;
				existing.y = powerup.y;
			} else {
				this.addPowerup({
					id: powerup.id,
					x: powerup.x,
					y: powerup.y,
					radius: powerup.radius,
					color: powerup.color,
					powerupType: powerup.powerupType as PowerupType,
				});
			}
		});

		// Remove old powerups
		const toRemove: string[] = [];
		this.powerups.forEach((_, id) => {
			if (!seenIds.has(id)) toRemove.push(id);
		});

		toRemove.forEach((id) => this.removePowerup(id));
		this.dirty = true;
	}

	/**
	 * Add a single powerup item
	 */
	addPowerup(powerup: PowerupItem): void {
		this.powerups.set(powerup.id, { ...powerup });
		if (this.texture) {
			this.createPowerupSprite(powerup);
		}
	}

	/**
	 * Remove a powerup item by ID
	 */
	removePowerup(id: string): void {
		this.powerups.delete(id);

		// Remove sprite
		const sprite = this.sprites.get(id);
		if (sprite) {
			sprite.destroy();
			this.sprites.delete(id);
		}

		// Remove label
		const label = this.labels.get(id);
		if (label) {
			this.labelContainer.removeChild(label);
			label.destroy();
			this.labels.delete(id);
		}
	}

	/**
	 * Create a sprite for a powerup item
	 */
	private createPowerupSprite(powerup: PowerupItem): void {
		if (!this.texture) return;

		// Determine grid coordinates
		const mapping = POWERUP_SPRITE_MAPPING[powerup.powerupType] || [0, 0];
		const [row, col] = mapping;

		// Create texture frame
		const frame = new Rectangle(
			col * SPRITE_SIZE,
			row * SPRITE_SIZE,
			SPRITE_SIZE,
			SPRITE_SIZE
		);

		const texture = new Texture({ source: this.texture.source, frame });
		const sprite = new Sprite(texture);

		sprite.anchor.set(0.5);
		// Scale sprite relative to powerup radius (icon should fit inside)
		const scale = (powerup.radius * 2 * 0.8) / SPRITE_SIZE;
		sprite.scale.set(scale);

		sprite.x = powerup.x;
		sprite.y = powerup.y;

		this.spriteContainer.addChild(sprite);
		this.sprites.set(powerup.id, sprite);
	}

	/**
	 * Get powerup count
	 */
	getPowerupCount(): number {
		return this.powerups.size;
	}

	/**
	 * Get all powerup items
	 */
	getAllPowerups(): PowerupItem[] {
		return Array.from(this.powerups.values());
	}

	/**
	 * Get powerups near a position
	 */
	getPowerupsNear(x: number, y: number, radius: number): PowerupItem[] {
		const nearby: PowerupItem[] = [];
		this.powerups.forEach((powerup) => {
			const dx = powerup.x - x;
			const dy = powerup.y - y;
			const dist = Math.sqrt(dx * dx + dy * dy);
			if (dist < radius + powerup.radius) {
				nearby.push(powerup);
			}
		});
		return nearby;
	}

	/**
	 * Update a powerup item position
	 */
	updatePowerupPosition(id: string, x: number, y: number): void {
		const powerup = this.powerups.get(id);
		if (powerup) {
			powerup.x = x;
			powerup.y = y;
			this.dirty = true;
		}
	}

	/**
	 * Check if a powerup item is visible
	 */
	private isVisible(x: number, y: number, radius: number): boolean {
		if (!this.visibleBounds) return true;

		const { left, right, top, bottom } = this.visibleBounds;
		return (
			x + radius >= left &&
			x - radius <= right &&
			y + radius >= top &&
			y - radius <= bottom
		);
	}

	/**
	 * Update animation and redraw
	 */
	update(dt: number): void {
		this.animTime += dt * this.config.animationSpeed;
		if (this.dirty || this.config.enablePulse) {
			this.draw();
			this.dirty = false;
		}
	}

	/**
	 * Draw all powerup items
	 */
	private draw(): void {
		const g = this.graphics;
		const glow = this.glowGraphics;

		g.clear();
		glow.clear();

		// Pulse effect
		const pulse = this.config.enablePulse
			? 1 + Math.sin(this.animTime * 3) * 0.1
			: 1;

		// Check for new sprites
		if (this.texture && this.sprites.size < this.powerups.size) {
			this.powerups.forEach((powerup) => {
				if (!this.sprites.has(powerup.id)) {
					this.createPowerupSprite(powerup);
				}
			});
		}

		this.powerups.forEach((powerup) => {
			if (!this.isVisible(powerup.x, powerup.y, powerup.radius * 2)) {
				if (this.sprites.has(powerup.id)) this.sprites.get(powerup.id)!.visible = false;
				return;
			}

			const sprite = this.sprites.get(powerup.id);
			if (sprite) {
				sprite.visible = true;
				sprite.x = powerup.x;
				sprite.y = powerup.y;

				// Apply pulse scale
				const baseScale = (powerup.radius * 2 * 0.8) / SPRITE_SIZE;
				sprite.scale.set(baseScale * pulse);
			}

			// Draw background circle (glow)
			glow.circle(powerup.x, powerup.y, powerup.radius * (0.8 + pulse * 0.2));
			glow.fill({ color: powerup.color, alpha: this.config.glowIntensity });

			// Draw outer ring
			g.circle(powerup.x, powerup.y, powerup.radius);
			g.stroke({ color: powerup.color, width: 2, alpha: 0.8 });

			// If text labels enabled (debug or fallback), update them
			if (this.config.showLabels) {
				// Legacy label logic omitted for brevity as sprites supersede it
			}
		});
	}

	/**
	 * Force redraw all powerups
	 */
	redraw(): void {
		this.dirty = true;
		this.draw();
	}

	/**
	 * Clear all powerup items
	 */
	clear(): void {
		this.powerups.clear();
		this.graphics.clear();
		this.glowGraphics.clear();

		this.sprites.forEach(sprite => sprite.destroy());
		this.sprites.clear();

		this.labels.forEach((label) => {
			this.labelContainer.removeChild(label);
			label.destroy();
		});
		this.labels.clear();
	}

	/**
	 * Clean up resources
	 */
	destroy(): void {
		this.clear();
		this.graphics.destroy();
		this.glowGraphics.destroy();
		this.labelContainer.destroy({ children: true });
		this.spriteContainer.destroy({ children: true });
		this.container.destroy({ children: true });
	}
}
